import { useState } from 'react';
import BaseCard from './BaseCard';
import { motion } from 'motion/react';
import { ChevronsLeft, ChevronsRight, Pencil } from 'lucide-react';
import { useDebounce } from 'react-use';
import cx from 'classnames';

type EntityItem = {
  key: React.Key;
  title: string;
  titleExtra?: React.ReactNode;
  subtitle?: string;
  onClick: () => void;
};

type EntityListCardProps<T> = {
  title?: string;
  subtitle?: string;
  items: T[];
  itemToEntityItem: (item: T) => EntityItem;
  isLoading?: boolean;
  onSearchQueryChange?: (query: string) => void;
  onPageChange?: (page: number) => void;
  currentPage?: number;
  totalPages?: number;
  AddOrUpdateItemFormComponent?: ({
    item,
    onCreate,
    onUpdate,
    onDelete,
  }: {
    item?: T;
    onCreate: () => void;
    onUpdate: () => void;
    onDelete: () => void;
  }) => React.ReactNode;
  onAdd?: () => void;
  onEdit?: (item: T) => void;
  canCreate?: boolean;
  canUpdate?: boolean;
};

export default function EntityListCard<T>({
  title,
  subtitle,
  items,
  itemToEntityItem,
  isLoading,
  onSearchQueryChange,
  onPageChange,
  currentPage,
  totalPages,
  AddOrUpdateItemFormComponent,
  onAdd,
  onEdit,
  canCreate,
  canUpdate,
}: EntityListCardProps<T>) {
  const [mode, setMode] = useState<'list' | 'creating' | 'editing'>('list');
  const [editingItem, setEditingItem] = useState<T | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');

  useDebounce(
    () => {
      onSearchQueryChange?.(searchQuery);
      onPageChange?.(1);
    },
    500,
    [searchQuery],
  );

  return (
    <BaseCard
      title={title}
      subtitle={subtitle}
      unpadded
      className="w-full"
      headerRightComponent={
        (AddOrUpdateItemFormComponent || onAdd) &&
        canCreate && (
          <motion.button
            className="text-xl font-medium"
            animate={mode}
            variants={{
              list: { rotate: 0 },
              creating: { rotate: 45 },
              editing: { rotate: 45 },
            }}
            onClick={() => {
              setMode(mode === 'list' ? 'creating' : 'list');
              if (mode === 'list') {
                setEditingItem(undefined);
              }
            }}
          >
            +
          </motion.button>
        )
      }
    >
      <motion.div
        key={mode}
        initial={{ x: mode === 'list' ? -100 : 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: mode === 'list' ? -100 : 100, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {mode === 'list' ? (
          <div>
            <div className="flex items-center justify-center p-4">
              <input
                type="text"
                placeholder="Search"
                className="w-full rounded-md border p-2"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <EntityList
              isLoading={isLoading}
              items={items}
              itemToEntityItem={itemToEntityItem}
              onEdit={
                (AddOrUpdateItemFormComponent || onEdit) &&
                (canUpdate || undefined) &&
                ((item: T) => {
                  setMode('editing');
                  setEditingItem(item);
                })
              }
            />
            {totalPages && currentPage && onPageChange && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            )}
          </div>
        ) : (
          AddOrUpdateItemFormComponent && (
            <AddOrUpdateItemFormComponent
              item={editingItem}
              onCreate={() => {
                setMode('list');
              }}
              onUpdate={() => {
                setMode('list');
              }}
              onDelete={() => {
                setMode('list');
              }}
            />
          )
        )}
      </motion.div>
    </BaseCard>
  );
}

function EntityList<T>({
  isLoading,
  items,
  itemToEntityItem,
  onEdit,
}: {
  isLoading?: boolean;
  items: T[];
  itemToEntityItem: (item: T) => EntityItem;
  onEdit?: (item: T) => void;
}) {
  return (
    <div>
      {isLoading ? (
        <div className="flex h-80 w-full items-center justify-center">
          <p className="text-sm italic text-neutral-500">Loading...</p>
        </div>
      ) : (
        <div className="min-h-80 divide-y divide-solid divide-neutral-300 border-b border-t border-solid border-neutral-300">
          {items.map(item => {
            const entityItem = itemToEntityItem(item);
            return (
              <EntityItemComponent
                key={entityItem.key}
                title={entityItem.title}
                titleExtra={entityItem.titleExtra}
                subtitle={entityItem.subtitle}
                onClick={entityItem.onClick}
                onEdit={onEdit && (() => onEdit(item))}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const totalPagesShown = Math.min(totalPages, 6);
  const minPageShown =
    totalPagesShown == totalPages
      ? 0
      : Math.max(
          currentPage -
            totalPagesShown / 2 -
            Math.max(totalPagesShown / 2 - 1 - (totalPages - currentPage), 0),
          0,
        );
  const maxPageShown =
    totalPagesShown == totalPages
      ? totalPages
      : Math.min(
          currentPage + totalPagesShown / 2 - 1 + Math.max(totalPagesShown / 2 - currentPage, 0),
          totalPages,
        );

  const shownPageOptions = Array.from({ length: totalPages }, (_, i) => i + 1).slice(
    minPageShown,
    maxPageShown,
  );

  return (
    <div className="flex items-center justify-center gap-2 p-2 text-sm font-medium">
      {minPageShown > 0 && (
        <button onClick={() => onPageChange(1)}>
          <ChevronsLeft className="text-neutral-400" />
        </button>
      )}
      {shownPageOptions.map(page => (
        <motion.button
          key={page}
          onClick={() => onPageChange(page)}
          className={cx(
            'rounded-full px-3 py-2',
            currentPage === page ? 'bg-primary text-neutral-100' : '',
          )}
          animate={currentPage === page ? 'active' : 'inactive'}
          variants={{
            active: { scale: 1.1 },
            inactive: { scale: 1 },
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {page}
        </motion.button>
      ))}
      {totalPages > maxPageShown && (
        <button onClick={() => onPageChange(totalPages)}>
          <ChevronsRight className="text-neutral-400" />
        </button>
      )}
    </div>
  );
}

function EntityItemComponent({
  title,
  titleExtra,
  subtitle,
  onClick,
  onEdit,
}: {
  title: string;
  titleExtra?: React.ReactNode;
  subtitle?: string;
  onClick: () => void;
  onEdit?: () => void;
}) {
  return (
    <div
      className="group flex h-16 cursor-pointer items-center justify-between px-5"
      onClick={onClick}
    >
      <div className="flex flex-col justify-center">
        <div className="flex items-center gap-2">
          <p className="text-md font-medium text-neutral-900">{title}</p>
          {titleExtra}
        </div>
        <p className="text-md line-clamp-1 italic text-neutral-500">{subtitle}</p>
      </div>
      {onEdit && (
        <button
          onClick={e => {
            e.stopPropagation();
            onEdit();
          }}
          className="duration-50 text-transparent transition-colors ease-in-out group-hover:text-neutral-500"
        >
          <Pencil className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
